import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import React from "react";

const styles = {
  jsonBox: {
    m: 0,
    p: 1.5,
    bgcolor: "grey.100",
    borderRadius: 1,
    fontFamily: "monospace",
    fontSize: 12,
    overflowX: "auto",
    maxHeight: "60vh",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    width: "100%",
  },
} satisfies Record<string, SxProps<Theme>>;

/**
 * A collapsible section wrapping a pretty-printed JSON payload — the debug pane's
 * building block for the dispatch / echoed context bags. A nullish value renders
 * a muted placeholder rather than an empty box, so an un-populated bag (a run
 * that has not completed yet) reads as "nothing here" instead of broken.
 *
 * @param props.title The section label shown on the collapsed summary.
 * @param props.value The JSON-shaped payload to render (may be null/undefined).
 * @param props.defaultExpanded Whether the section starts open.
 */
const CollapsibleJson = ({
  title,
  value,
  defaultExpanded = false,
}: {
  readonly title: string;
  readonly value: unknown;
  readonly defaultExpanded?: boolean;
}) => (
  <Accordion disableGutters defaultExpanded={defaultExpanded}>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography variant="subtitle2">{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>
      {isNil(value) ? (
        <Typography variant="body2" color="text.secondary">
          Not recorded.
        </Typography>
      ) : (
        <Box component="pre" sx={styles.jsonBox}>
          {JSON.stringify(value, null, 2)}
        </Box>
      )}
    </AccordionDetails>
  </Accordion>
);

export default CollapsibleJson;
